import { useUserPermissions } from "@dashboard/auth/hooks/useUserPermissions";
import { DashboardCard } from "@dashboard/components/Card";
import { CardSpacer } from "@dashboard/components/CardSpacer";
import { hasPermissions } from "@dashboard/components/RequirePermissions";
import { PermissionEnum } from "@dashboard/graphql";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { prepareCheckoutMessages as messages } from "./messages";
import { QuotedFreightField } from "./QuotedFreightField";
import {
  PrepareCheckoutCustomer,
  PrepareCheckoutDisabledReason,
  usePrepareCheckout,
} from "./usePrepareCheckout";

const disabledMessages = {
  "no-storefront": messages.noStorefront,
  "no-email": messages.noEmail,
  "no-channel": messages.noChannel,
} satisfies Record<NonNullable<PrepareCheckoutDisabledReason>, unknown>;

export interface PrepareCheckoutCardProps {
  customer?: PrepareCheckoutCustomer | null;
}

export const PrepareCheckoutCard = ({ customer }: PrepareCheckoutCardProps) => {
  const intl = useIntl();
  const userPermissions = useUserPermissions();
  const {
    prepare,
    loading,
    error,
    disabledReason,
    channelSlug,
    currencyCode,
    isNonDefaultChannel,
    preparedCheckoutId,
  } = usePrepareCheckout(customer);

  if (!hasPermissions(userPermissions ?? [], [PermissionEnum.IMPERSONATE_USER])) {
    return null;
  }

  return (
    <>
      <CardSpacer />
      <DashboardCard data-test-id="prepare-checkout-card">
        <DashboardCard.Header>
          <DashboardCard.Title>{intl.formatMessage(messages.title)}</DashboardCard.Title>
        </DashboardCard.Header>
        <DashboardCard.Content>
          <Box display="flex" flexDirection="column" gap={3}>
            <Text size={2}>{intl.formatMessage(messages.description)}</Text>
            {isNonDefaultChannel && (
              <Text size={2} color="default2" data-test-id="prepare-checkout-channel">
                {intl.formatMessage(messages.channelLine, { channel: channelSlug })}
              </Text>
            )}
            <Box>
              <Button
                variant="secondary"
                data-test-id="prepare-checkout-button"
                disabled={!!disabledReason || loading}
                onClick={prepare}
              >
                {intl.formatMessage(loading ? messages.creating : messages.button)}
              </Button>
            </Box>
            {disabledReason && (
              <Text size={2} color="default2" data-test-id="prepare-checkout-disabled-reason">
                {intl.formatMessage(disabledMessages[disabledReason])}
              </Text>
            )}
            {error && (
              <Text size={2} color="critical1" data-test-id="prepare-checkout-error">
                {error}
              </Text>
            )}
            {preparedCheckoutId && (
              <QuotedFreightField
                key={preparedCheckoutId}
                checkoutId={preparedCheckoutId}
                currencyCode={currencyCode}
              />
            )}
          </Box>
        </DashboardCard.Content>
      </DashboardCard>
    </>
  );
};

PrepareCheckoutCard.displayName = "PrepareCheckoutCard";
